import { getDocument } from '@atlaskit/browser-apis';
import type { Config } from '@atlaskit/react-ufo/config';
import {
	init,
	type GenericAnalyticWebClientPromise,
} from '@atlaskit/react-ufo/interaction-metrics-init';
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import { initAnalyticsClient } from '@atlassian/twg-microsite-experience/analytics-client';
import { getConfig } from '@atlassian/twg-microsite-experience/config';
import {
	HOME_ROUTE_NAME,
	HOME_ROUTE_PATH,
	micrositeRouteNameForPath,
	type MicrositeRouteName,
} from '@atlassian/twg-microsite-experience/routes';

import { resolveInitialLocation } from '../../ui/routes/initial-location';

const kind = {
	page_load: 1,
	transition: 1,
	press: 1,
	typing: 0,
	legacy: 0,
	hover: 0,
	segment: 0,
};

const { env, region } = getConfig();

const config: Config = {
	product: 'teamworkGraph',
	region,
	capability: {
		// `feature_flag_access: 0` for now — wiring `addFeatureFlagAccessed`
		// into the feature-flag layer is a follow-up. Without that hook
		// `feature_flag_access: 1` would advertise a capability we don't
		// fulfil yet.
		feature_flag_access: 0,
		react_profiler: 1,
	},
	kind,
	vc: {
		enabled: true,
		// Default (100) is plenty for a marketing-style page; reduces VC
		// compute on every paint, which matters more on mobile / low-end
		// devices that often hit landing pages.
		heatmapSize: 100,
		oldDomUpdates: false,
		// Devtools surface should never ship to real users — gate to
		// non-prod to keep the prod bundle lean and avoid leaking
		// internal UFO state on `window`.
		devToolsEnabled: env !== 'production',
		selectorConfig: {
			id: false,
			role: true,
			className: true,
			testId: true,
		},
	},
};

/**
 * The route this page load is for.
 *
 * Matched rather than compared against a handful of literal paths, so a
 * parameterised route reports itself rather than falling through to home:
 * `/benchmark/{cloudId}/runs/{runId}` is a benchmark page load, and tracing it
 * as a home page load would fold two very different loads into one metric.
 */
const getInitialRouteName = (): MicrositeRouteName => {
	const location = getDocument()?.location;
	const { pathname } = resolveInitialLocation({
		pathname: location?.pathname ?? HOME_ROUTE_PATH,
		search: location?.search ?? '',
		hash: location?.hash ?? '',
	});

	return micrositeRouteNameForPath(pathname) ?? HOME_ROUTE_NAME;
};

/**
 * Boot React UFO for the TwG Microsite.
 *
 * Must be called *before* `createRoot(...).render(<App />)` in `index.tsx`.
 * Reuses the singleton `AnalyticsWebClient` returned by `initAnalyticsClient`
 * so UFO and product analytics share one transport.
 */
export const initUFO = (): void => {
	const client = initAnalyticsClient() as unknown as GenericAnalyticWebClientPromise;
	init(Promise.resolve(client), config);

	const routeName = getInitialRouteName();
	traceUFOPageLoad(routeName, routeName);
};
